import _uniq from "lodash/uniq";
import _isArray from "lodash/isArray";
import _isPlainObject from "lodash/isPlainObject";
import _isNil from "lodash/isNil";
import cx from 'classnames';
import * as React from 'react';
import { mergeStyles } from './mergeThemes';
// It's only necessary to map props that don't use 'children' as value ('children' is the default)
var mappedProps = {
  iframe: 'src',
  img: 'src',
  input: 'type' // ============================================================
  // Factories
  // ============================================================

  /** A more robust React.createElement. It can create elements from primitive values. */

};
export function createShorthand(_ref) {
  var allowsJSX = _ref.allowsJSX,
      Component = _ref.Component,
      mappedProp = _ref.mappedProp,
      mappedArrayProp = _ref.mappedArrayProp,
      valueOrRenderCallback = _ref.valueOrRenderCallback,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? {} : _ref$options;
  var valIsRenderFunction = typeof valueOrRenderCallback === 'function' && !React.isValidElement(valueOrRenderCallback);

  if (valIsRenderFunction) {
    return createShorthandFromRenderCallback({
      allowsJSX: allowsJSX,
      Component: Component,
      renderCallback: valueOrRenderCallback,
      mappedProp: mappedProp,
      mappedArrayProp: mappedArrayProp,
      options: options
    });
  }

  return createShorthandFromValue({
    allowsJSX: allowsJSX,
    Component: Component,
    mappedProp: mappedProp,
    mappedArrayProp: mappedArrayProp,
    value: valueOrRenderCallback,
    options: options
  });
}
export function createShorthandFactory(_ref2) {
  var Component = _ref2.Component,
      mappedProp = _ref2.mappedProp,
      mappedArrayProp = _ref2.mappedArrayProp,
      allowsJSX = _ref2.allowsJSX;

  if (typeof Component !== 'function' && typeof Component !== 'string') {
    throw new Error('createShorthandFactory() Component must be a string or function.');
  }

  return function (val, options) {
    return createShorthand({
      Component: Component,
      mappedProp: mappedProp,
      mappedArrayProp: mappedArrayProp,
      allowsJSX: allowsJSX,
      valueOrRenderCallback: val,
      options: options
    });
  };
} // ============================================================
// Private Utils
// ============================================================

function createShorthandFromValue(_ref3) {
  var Component = _ref3.Component,
      mappedProp = _ref3.mappedProp,
      mappedArrayProp = _ref3.mappedArrayProp,
      value = _ref3.value,
      options = _ref3.options,
      _ref3$allowsJSX = _ref3.allowsJSX,
      allowsJSX = _ref3$allowsJSX === void 0 ? true : _ref3$allowsJSX;

  if (typeof Component !== 'function' && typeof Component !== 'string') {
    throw new Error('createShorthand() Component must be a string or function.');
  } // short circuit noop values


  var valIsNoop = _isNil(value) || typeof value === 'boolean';
  if (valIsNoop && !options.render) return null;
  var valIsPrimitive = typeof value === 'string' || typeof value === 'number';

  var valIsPropsObject = _isPlainObject(value);

  var valIsArray = _isArray(value);

  var valIsReactElement = React.isValidElement(value); // unhandled type warning

  if (process.env.NODE_ENV !== 'production') {
    var displayName = typeof Component === 'string' ? Component : Component.displayName;

    if (!valIsPrimitive && !valIsPropsObject && !valIsArray && !valIsReactElement && !valIsNoop) {
      /* eslint-disable-next-line no-console */
      console.error(["The shorthand prop for \"".concat(displayName, "\" component was passed a JSX element but this slot only supports string|number|object|array|ReactElements."), ' Use null|undefined|boolean for none.', " Received: ".concat(value)].join(''));
    }

    if (!allowsJSX && valIsReactElement) {
      /* eslint-disable-next-line no-console */
      console.error(["The shorthand prop for \"".concat(displayName, "\" component was passed a JSX element but this slot only supports string|number|object|array."), ' Use null|undefined|boolean for none.', " Received: ".concat(value)].join(''));
    }
  } // ----------------------------------------
  // Build up props
  // ----------------------------------------


  var defaultProps = options.defaultProps ? options.defaultProps() : {}; // User's props

  var usersProps = valIsReactElement && {} || valIsPropsObject && value || {}; // Override props

  var overrideProps = typeof options.overrideProps === 'function' ? options.overrideProps(Object.assign({}, defaultProps, usersProps)) : options.overrideProps || {}; // Merge props

  var props = Object.assign({}, defaultProps, usersProps, overrideProps);
  var mappedHTMLProps = mappedProps[overrideProps.as || defaultProps.as]; // Map prop for primitive value

  if (valIsPrimitive || valIsReactElement) {
    props[mappedHTMLProps || mappedProp || 'children'] = value;
  } // Map prop for array value


  if (valIsArray) {
    props[mappedHTMLProps || mappedArrayProp || 'children'] = value;
  } // Merge className


  if (defaultProps.className || overrideProps.className || usersProps.className) {
    var mergedClassesNames = cx(defaultProps.className, overrideProps.className, usersProps.className);
    props.className = _uniq(mergedClassesNames.split(' ')).join(' ');
  } // Merge style


  if (defaultProps.style || overrideProps.style || usersProps.style) {
    props.style = Object.assign({}, defaultProps.style, usersProps.style, overrideProps.style);
  } // Merge styles


  if (defaultProps.styles || overrideProps.styles || usersProps.styles) {
    props.styles = mergeStyles(defaultProps.styles, usersProps.styles, overrideProps.styles);
  } // ----------------------------------------
  // Get key
  // ----------------------------------------


  var _options$generateKey = options.generateKey,
      generateKey = _options$generateKey === void 0 ? true : _options$generateKey; // Use key or generate key

  if (generateKey && _isNil(props.key)) {
    if (valIsPrimitive) {
      // use string/number shorthand values as the key
      props.key = value;
    }

    if (valIsReactElement) {
      // use the key from React Element
      var elementKey = value.key; // <div /> - key is not passed as will be `null`
      // <div key={null} /> - key is passed as `null` and will be stringified

      var isNullKey = elementKey === null;

      if (!isNullKey) {
        props.key = elementKey;
      }
    }
  } // Remove the kind prop from the props object


  delete props.kind; // ----------------------------------------
  // Create Element
  // ----------------------------------------

  var render = options.render;

  if (render) {
    return render(Component, props);
  }

  if (!allowsJSX && valIsReactElement) {
    return null;
  } // Create ReactElements from built up props


  if (valIsPrimitive || valIsPropsObject || valIsArray || valIsReactElement) {
    return React.createElement(Component, props);
  }

  return null;
}

function createShorthandFromRenderCallback(_ref4) {
  var Component = _ref4.Component,
      renderCallback = _ref4.renderCallback,
      mappedProp = _ref4.mappedProp,
      mappedArrayProp = _ref4.mappedArrayProp,
      allowsJSX = _ref4.allowsJSX,
      options = _ref4.options;

  var render = function render(shorthandValue, renderTree) {
    return createShorthandFromValue({
      Component: Component,
      mappedProp: mappedProp,
      mappedArrayProp: mappedArrayProp,
      allowsJSX: allowsJSX,
      value: shorthandValue,
      options: Object.assign({}, options, renderTree && {
        render: renderTree
      })
    });
  };

  return renderCallback(render);
}